package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import co.maytes.standupbot.dashboard.styles.ControlClass
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.dashboard.styles.tabularNums
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input

val TextInputStyle = CssStyle(extraModifier = Modifier.classNames(ControlClass.FIELD)) {
    base {
        Modifier
            .fontSize(Typography.body.size)
            .styleModifier {
                property("font-variant-numeric", "tabular-nums")
                property("min-height", "44px")
                property("padding", "10px 14px")
                property("line-height", "1.4")
            }
    }
}

@Composable
fun TextInput(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    help: String? = null,
    error: String? = null,
    placeholder: String = "",
    disabled: Boolean = false,
    size: ControlSize = ControlSize.Md,
    type: InputType<String> = InputType.Text,
    ariaLabel: String? = null,
    hint: String? = null,
    required: Boolean = false,
    loading: Boolean = false,
    clearable: Boolean = true,
    numeric: Boolean = false,
    reserveMessageSpace: Boolean = false,
) {
    val metrics = size.metrics
    val holder = remember { ElementHolder() }
    val clearAllowed = clearable && !disabled && type != InputType.Password

    FormField(
        label = label,
        modifier = modifier,
        hint = hint,
        help = help,
        error = error,
        required = required,
        reserveMessageSpace = reserveMessageSpace,
    ) { field ->
        val slots = listOf(clearAllowed, loading, field.invalid).count { it }
        Div(
            attrs = Modifier
                .styleModifier {
                    property("position", "relative")
                    property("width", "100%")
                }
                .toAttrs(),
        ) {
            Input(
                type = type,
                attrs = fieldBoxLook(metrics, slots)
                    .styleModifier { property("width", "100%") }
                    .let { if (numeric || type == InputType.Tel) it.tabularNums() else it }
                    .toAttrs {
                        value(value)
                        if (placeholder.isNotEmpty()) placeholder(placeholder)
                        onInput { onValueChange(it.value) }
                        if (disabled) attr("disabled", "")
                        if (loading) attr("aria-busy", "true")
                        if (numeric) attr("inputmode", "numeric")
                        ariaLabel?.let { attr("aria-label", it) }
                        fieldControl(field)
                        ref { element ->
                            holder.element = element
                            onDispose { if (holder.element === element) holder.element = null }
                        }
                    },
            )
            if (slots > 0) {
                FieldAdornments(metrics.padX) {
                    if (loading) AdornmentSlot { Spinner(sizePx = AdornmentIconPx, ariaLabel = "") }
                    if (clearAllowed) {
                        ClearAdornment(
                            visible = value.isNotEmpty(),
                            onClear = {
                                onValueChange("")
                                holder.element?.focus()
                            },
                        )
                    }
                    if (field.invalid) InvalidAdornment()
                }
            }
        }
    }
}
